import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import { toastError, toastSuccess } from "../utils/toast";

const monthNames = [
  "Gennaio",
  "Febbraio",
  "Marzo",
  "Aprile",
  "Maggio",
  "Giugno",
  "Luglio",
  "Agosto",
  "Settembre",
  "Ottobre",
  "Novembre",
  "Dicembre",
];

const giorniSettimana = [
  null, // placeholder to use 1-based indexing
  "Lunedì",
  "Martedì",
  "Mercoledì",
  "Giovedì",
  "Venerdì",
  "Sabato",
  "Domenica",
];

const emptyForm = {
  giorno_settimana: 1,
  ora_inizio: "",
  luogo: "",
  indirizzo: "",
  note: "",
  stato: "attiva",
};

const formatScheduleDate = (dateValue) => {
  if (!dateValue) return "Data non disponibile";
  const date = new Date(`${String(dateValue).slice(0, 10)}T12:00:00`);
  return new Intl.DateTimeFormat("it-IT", {
    weekday: "short",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
};

const getAssignedLeaderName = (assignment) =>
  [assignment?.conduttore?.nome, assignment?.conduttore?.cognome]
    .filter(Boolean)
    .join(" ") || "Conduttore non disponibile";

const AdunanzeServizio = () => {
  const { user, activeCongregazione } = useAuth();
  const [adunanze, setAdunanze] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [savingModal, setSavingModal] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [resettingAvailability, setResettingAvailability] = useState(false);
  const now = new Date();
  const [resetMonth, setResetMonth] = useState(now.getMonth());
  const [resetYear, setResetYear] = useState(String(now.getFullYear()));
  const [assignmentMonth, setAssignmentMonth] = useState(now.getMonth());
  const [assignmentYear, setAssignmentYear] = useState(String(now.getFullYear()));
  const [assignedTurns, setAssignedTurns] = useState([]);
  const [assignedTurnsLoading, setAssignedTurnsLoading] = useState(true);

  const canManage =
    user?.ruolo === "admin" || user?.ruolo === "super_admin" || false;
  const canViewAssignedTurns = canManage || user?.service_leader === true;

  const loadAdunanze = async () => {
    if (!activeCongregazione?.id) {
      setAdunanze([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/adunanze-servizio`);
      setAdunanze(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Errore caricamento adunanze:", error);
      toastError(
        error.response?.data?.message ||
          "Errore nel caricamento delle adunanze"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdunanze();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCongregazione?.id, canManage]);

  const getAssignmentRange = () => {
    const parsedYear = parseInt(assignmentYear, 10);
    const safeYear = Number.isFinite(parsedYear)
      ? parsedYear
      : now.getFullYear();
    const startDate = new Date(Date.UTC(safeYear, assignmentMonth, 1));
    const endDate = new Date(Date.UTC(safeYear, assignmentMonth + 1, 0));
    return {
      startStr: startDate.toISOString().slice(0, 10),
      endStr: endDate.toISOString().slice(0, 10),
      label: `${monthNames[assignmentMonth]} ${safeYear}`,
    };
  };

  const loadAssignedTurns = async () => {
    if (!activeCongregazione?.id || !canViewAssignedTurns) {
      setAssignedTurns([]);
      setAssignedTurnsLoading(false);
      return;
    }

    try {
      setAssignedTurnsLoading(true);
      const { startStr, endStr } = getAssignmentRange();
      const response = await api.get("/adunanze-servizio/turni/consultazione", {
        params: {
          congregazione_id: activeCongregazione.id,
          data_inizio: startStr,
          data_fine: endStr,
        },
      });
      setAssignedTurns(
        Array.isArray(response.data?.assegnazioni)
          ? response.data.assegnazioni
          : []
      );
    } catch (error) {
      console.error("Errore caricamento turni assegnati:", error);
      setAssignedTurns([]);
      toastError(
        error.response?.data?.message ||
          "Errore nel caricamento dei turni assegnati"
      );
    } finally {
      setAssignedTurnsLoading(false);
    }
  };

  useEffect(() => {
    loadAssignedTurns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeCongregazione?.id,
    canViewAssignedTurns,
    assignmentMonth,
    assignmentYear,
  ]);

  const openModal = (adunanza = null) => {
    if (adunanza) {
      setFormData({
        giorno_settimana: adunanza.giorno_settimana || 1,
        ora_inizio: adunanza.ora_inizio
          ? adunanza.ora_inizio.substring(0, 5)
          : "",
        luogo: adunanza.luogo || "",
        indirizzo: adunanza.indirizzo || "",
        note: adunanza.note || "",
        stato: adunanza.stato || "attiva",
      });
      setEditing(adunanza);
    } else {
      setFormData({
        ...emptyForm,
        giorno_settimana: 1,
        stato: "attiva",
      });
      setEditing(null);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setFormData(emptyForm);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const saveAdunanza = async () => {
    try {
      setSavingModal(true);
      const payload = {
        giorno_settimana: Number(formData.giorno_settimana),
        ora_inizio: formData.ora_inizio,
        luogo: formData.luogo,
        indirizzo: formData.indirizzo || "",
        note: formData.note || "",
        stato: formData.stato || "attiva",
      };

      if (!payload.giorno_settimana || !payload.ora_inizio || !payload.luogo) {
        toastError("Giorno, orario e luogo sono obbligatori");
        setSavingModal(false);
        return;
      }

      if (editing) {
        await api.put(`/adunanze-servizio/${editing.id}`, payload);
        toastSuccess("Adunanza aggiornata con successo");
      } else {
        await api.post("/adunanze-servizio", payload);
        toastSuccess("Adunanza creata con successo");
      }

      closeModal();
      loadAdunanze();
    } catch (error) {
      console.error("Errore salvataggio adunanza:", error);
      toastError(error.response?.data?.message || "Errore durante il salvataggio");
    } finally {
      setSavingModal(false);
    }
  };

  const deleteAdunanza = async (id) => {
    if (
      !window.confirm(
        "Sei sicuro di voler eliminare questa adunanza per il servizio?"
      )
    ) {
      return;
    }
    try {
      await api.delete(`/adunanze-servizio/${id}`);
      toastSuccess("Adunanza eliminata");
      loadAdunanze();
    } catch (error) {
      console.error("Errore eliminazione adunanza:", error);
      toastError("Impossibile eliminare l'adunanza");
    }
  };

  const formattedAdunanze = useMemo(() => {
    return adunanze.map((adunanza) => ({
      ...adunanza,
      formattedDay:
        giorniSettimana[adunanza.giorno_settimana] || "Giorno non impostato",
      formattedTime: adunanza.ora_inizio
        ? adunanza.ora_inizio.substring(0, 5)
        : "",
    }));
  }, [adunanze]);

  const handleToggleStato = async (adunanza) => {
    if (!adunanza) return;
    try {
      setTogglingId(adunanza.id);
      const response = await api.patch(
        `/adunanze-servizio/${adunanza.id}/toggle-stato`
      );
      toastSuccess(
        response.data?.message || "Stato dell'adunanza aggiornato"
      );
      await loadAdunanze();
    } catch (error) {
      console.error("Errore toggle stato adunanza:", error);
      toastError(
        error.response?.data?.message ||
          "Errore durante l'aggiornamento dello stato"
      );
    } finally {
      setTogglingId(null);
    }
  };

  const getStatoBadgeClasses = (stato) => {
    if (stato === "attiva") {
      return "bg-green-100 text-green-800";
    }
    return "bg-gray-100 text-gray-600";
  };

  const getResetRange = () => {
    const parsedYear = parseInt(resetYear, 10);
    const safeYear = Number.isFinite(parsedYear) ? parsedYear : now.getFullYear();
    const startDate = new Date(Date.UTC(safeYear, resetMonth, 1));
    const endDate = new Date(Date.UTC(safeYear, resetMonth + 1, 0));
    return {
      startStr: startDate.toISOString().slice(0, 10),
      endStr: endDate.toISOString().slice(0, 10),
      labelYear: safeYear,
    };
  };

  const resetDisponibilita = async () => {
    const { startStr, endStr, labelYear } = getResetRange();
    const monthLabel = `${monthNames[resetMonth] || "Mese"} ${labelYear}`;

    const confirmed = window.confirm(
      `Saranno rimosse tutte le disponibilità registrate tra ${startStr} e ${endStr}. Vuoi procedere?`
    );
    if (!confirmed) {
      return;
    }

    try {
      setResettingAvailability(true);
      const response = await api.post("/adunanze-servizio/reset-disponibilita", {
        data_inizio: startStr,
        data_fine: endStr,
      });
      const removedCount = typeof response.data?.removed === "number"
        ? response.data.removed
        : null;
      const suffix = removedCount !== null ? ` (righe eliminate: ${removedCount})` : "";
      toastSuccess(
        response.data?.message ||
          `Disponibilità resettate per ${monthLabel}${suffix}`
      );
    } catch (error) {
      console.error("Errore reset disponibilità servizio:", error);
      toastError(
        error.response?.data?.message ||
          "Errore durante il reset delle disponibilità"
      );
    } finally {
      setResettingAvailability(false);
    }
  };

  if (!activeCongregazione?.id) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <p className="text-gray-600">
            Seleziona una congregazione per visualizzare le adunanze per il servizio.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
      <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
        <div className="max-w-2xl">
          <h1 className="text-3xl font-bold text-gray-900">
            Adunanze per il Servizio
          </h1>
          <p className="text-gray-600">
            Qui trovi il programma delle adunanze del servizio per la congregazione selezionata.
            {canManage
              ? " Puoi gestire luoghi, orari e disponibilità dei conduttori."
              : " Le informazioni sono consultabili per pianificare al meglio la partecipazione."}
          </p>
          <div className="accent-line mt-3" />
        </div>
        {canManage && (
          <div className="w-full xl:w-auto xl:min-w-[33rem] flex flex-col gap-3 xl:items-end">
            <button
              onClick={() => openModal()}
              className="inline-flex min-h-[44px] w-full sm:w-auto items-center justify-center px-5 py-2.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Nuova Adunanza
            </button>
            <section
              aria-label="Reset disponibilità conduttori"
              className="w-full rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_7rem] sm:gap-2">
                  <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                    Periodo da resettare
                    <select
                      aria-label="Mese reset disponibilità"
                      className="min-h-[42px] border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      value={resetMonth}
                      onChange={(e) => {
                        const parsed = parseInt(e.target.value, 10);
                        if (!Number.isNaN(parsed)) {
                          setResetMonth(parsed);
                        }
                      }}
                    >
                      {monthNames.map((label, index) => (
                        <option key={label} value={index}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                    Anno
                    <input
                      aria-label="Anno reset disponibilità"
                      type="number"
                      className="min-h-[42px] w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      value={resetYear}
                      onChange={(e) => setResetYear(e.target.value)}
                      min="2000"
                      max="2100"
                    />
                  </label>
                </div>
                <button
                  onClick={resetDisponibilita}
                  disabled={resettingAvailability}
                  className="inline-flex min-h-[42px] w-full sm:w-auto items-center justify-center px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 disabled:opacity-50"
                >
                  {resettingAvailability
                    ? "Reset in corso..."
                    : "Reset disponibilità A.S."}
                </button>
              </div>
            </section>
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 md:px-6 py-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-primary-600" />
            Luoghi e orari Adunanze
          </h2>
          <span className="text-sm text-gray-500">
            {formattedAdunanze.length} adunanze
          </span>
        </div>
        <div className="accent-line mx-4 md:mx-6" />
        {loading ? (
          <div className="p-4 md:p-6 text-center text-gray-600">
            Caricamento in corso...
          </div>
        ) : formattedAdunanze.length === 0 ? (
          <div className="p-4 md:p-6 text-center text-gray-600">
            Nessuna adunanza programmata nel periodo selezionato
          </div>
        ) : (
          <div className="p-4 md:p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {formattedAdunanze.map((adunanza) => (
                <div
                  key={`card_${adunanza.id}`}
                  className="relative border border-gray-200 rounded-lg p-4 pl-6 bg-white flex flex-col gap-2 transition hover:bg-gray-50 hover:border-primary-200 hover:shadow-sm"
                >
                  <span
                    className="accent-bar top-4 bottom-4"
                    aria-hidden="true"
                  />
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-primary-700 uppercase">
                        {adunanza.formattedDay}
                      </p>
                      <p className="text-lg font-bold text-gray-900">
                        {adunanza.formattedTime || "Orario da definire"}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${getStatoBadgeClasses(
                        adunanza.stato
                      )}`}
                    >
                      {adunanza.stato === "inattiva" ? "Disattivata" : "Attiva"}
                    </span>
                  </div>
                  <div>
                    <p className="text-base font-semibold text-gray-900">
                      {adunanza.luogo}
                    </p>
                    {adunanza.indirizzo && (
                      <p className="text-sm text-gray-600">
                        {adunanza.indirizzo}
                      </p>
                    )}
                  </div>
                  {adunanza.note && (
                    <p className="text-sm text-gray-700 bg-gray-50 rounded-md p-2">
                      {adunanza.note}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {canViewAssignedTurns && (
        <section
          aria-labelledby="turni-assegnati-heading"
          className="bg-white shadow rounded-lg overflow-hidden"
        >
          <div className="px-4 md:px-6 py-4 border-b border-gray-200 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2
                id="turni-assegnati-heading"
                className="text-lg font-medium text-gray-900 flex items-center gap-2"
              >
                <CalendarIcon className="h-5 w-5 text-primary-600" />
                Turni assegnati
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Consulta i conduttori già assegnati nei diversi luoghi.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_7rem] lg:w-[21rem]">
              <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                Mese consultato
                <select
                  aria-label="Mese turni assegnati"
                  className="min-h-[42px] border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={assignmentMonth}
                  onChange={(event) => {
                    const selectedMonth = parseInt(event.target.value, 10);
                    if (!Number.isNaN(selectedMonth)) {
                      setAssignmentMonth(selectedMonth);
                    }
                  }}
                >
                  {monthNames.map((label, index) => (
                    <option key={label} value={index}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                Anno
                <input
                  aria-label="Anno turni assegnati"
                  type="number"
                  className="min-h-[42px] w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={assignmentYear}
                  onChange={(event) => setAssignmentYear(event.target.value)}
                  min="2000"
                  max="2100"
                />
              </label>
            </div>
          </div>
          <div className="accent-line mx-4 md:mx-6" />

          {assignedTurnsLoading ? (
            <div className="p-5 md:p-6 text-center text-gray-600">
              Caricamento dei turni assegnati...
            </div>
          ) : assignedTurns.length === 0 ? (
            <div className="p-5 md:p-6 text-center text-gray-600">
              Nessun conduttore assegnato per {getAssignmentRange().label}.
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-200 md:hidden">
                {assignedTurns.map((assignment) => (
                  <article
                    key={assignment.id}
                    className="p-4 space-y-3"
                    aria-label={`Turno ${formatScheduleDate(assignment.data_occurrence)}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-gray-900 capitalize">
                          {formatScheduleDate(assignment.data_occurrence)}
                        </p>
                        <p className="text-sm text-gray-600">
                          {assignment.ora_inizio?.slice(0, 5) || "Orario da definire"}
                        </p>
                      </div>
                      <span className="inline-flex rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800">
                        Assegnato
                      </span>
                    </div>
                    <dl className="grid grid-cols-1 gap-2 text-sm">
                      <div>
                        <dt className="text-gray-500">Luogo</dt>
                        <dd className="font-medium text-gray-900">{assignment.luogo}</dd>
                        {assignment.indirizzo && (
                          <dd className="text-gray-600">{assignment.indirizzo}</dd>
                        )}
                      </div>
                      <div>
                        <dt className="text-gray-500">Conduttore assegnato</dt>
                        <dd className="font-medium text-gray-900">
                          {getAssignedLeaderName(assignment)}
                        </dd>
                      </div>
                    </dl>
                  </article>
                ))}
              </div>
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Orario
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Luogo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Conduttore assegnato
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {assignedTurns.map((assignment) => (
                      <tr key={assignment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 capitalize whitespace-nowrap">
                          {formatScheduleDate(assignment.data_occurrence)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                          {assignment.ora_inizio?.slice(0, 5) || "—"}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <p className="font-medium">{assignment.luogo}</p>
                          {assignment.indirizzo && (
                            <p className="mt-0.5 text-gray-500">{assignment.indirizzo}</p>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {getAssignedLeaderName(assignment)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}

      {canManage && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 md:px-6 py-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-primary-600" />
              Gestione luoghi e orari
            </h2>
            <span className="text-sm text-gray-500">
              {formattedAdunanze.length} adunanze
            </span>
          </div>
          <div className="accent-line mx-4 md:mx-6" />
          {loading ? (
            <div className="p-4 md:p-6 text-center text-gray-600">
              Caricamento in corso...
            </div>
          ) : formattedAdunanze.length === 0 ? (
            <div className="p-4 md:p-6 text-center text-gray-600">
              Nessuna adunanza programmata nel periodo selezionato
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-200 md:hidden">
                {formattedAdunanze.map((adunanza) => (
                  <article key={adunanza.id} className="p-4 space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="relative pl-3">
                        <span
                          className="accent-bar inset-y-0"
                          aria-hidden="true"
                        />
                        <p className="font-semibold text-gray-900 capitalize">
                          {adunanza.formattedDay}
                        </p>
                        <p className="text-sm text-gray-600">
                          {adunanza.formattedTime || "Orario da definire"}
                        </p>
                      </div>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatoBadgeClasses(
                          adunanza.stato
                        )}`}
                      >
                        {adunanza.stato === "inattiva" ? "disattivata" : "attiva"}
                      </span>
                    </div>

                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Luogo
                      </p>
                      <p className="mt-1 font-medium text-gray-900">{adunanza.luogo}</p>
                      {adunanza.indirizzo && (
                        <p className="text-sm text-gray-600">{adunanza.indirizzo}</p>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-3">
                      <label className="inline-flex items-center cursor-pointer select-none">
                        <span className="text-sm text-gray-600 mr-2">
                          {adunanza.stato === "attiva" ? "Attiva" : "Disattivata"}
                        </span>
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={adunanza.stato === "attiva"}
                          onChange={() => handleToggleStato(adunanza)}
                          disabled={togglingId === adunanza.id}
                        />
                        <div
                          className={`w-10 h-5 rounded-full transition-colors ${
                            adunanza.stato === "attiva" ? "bg-green-500" : "bg-gray-300"
                          }`}
                        >
                          <div
                            className={`h-5 w-5 bg-white rounded-full shadow transform transition-transform ${
                              adunanza.stato === "attiva" ? "translate-x-5" : "translate-x-0"
                            }`}
                          />
                        </div>
                      </label>
                      <div className="flex items-center gap-4">
                        <button
                          type="button"
                          aria-label={`Modifica adunanza ${adunanza.formattedDay}`}
                          onClick={() => openModal(adunanza)}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-800"
                        >
                          <PencilIcon className="h-5 w-5" />
                          Modifica
                        </button>
                        <button
                          type="button"
                          aria-label={`Elimina adunanza ${adunanza.formattedDay}`}
                          onClick={() => deleteAdunanza(adunanza.id)}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="h-5 w-5" />
                          Elimina
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
              <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Giorno
                  </th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Luogo
                  </th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Orario
                  </th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stato
                  </th>
                  <th className="px-4 md:px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {formattedAdunanze.map((adunanza) => (
                  <tr key={adunanza.id} className="hover:bg-gray-50">
                    <td className="px-4 md:px-6 py-4 relative pl-6">
                      <span
                        className="accent-bar top-3 bottom-3"
                        aria-hidden="true"
                      />
                      <p className="font-medium text-gray-900 capitalize">
                        {adunanza.formattedDay}
                      </p>
                    </td>
                    <td className="px-4 md:px-6 py-4">
                      <p className="text-gray-900">{adunanza.luogo}</p>
                      {adunanza.indirizzo && (
                        <p className="text-sm text-gray-500">
                          {adunanza.indirizzo}
                        </p>
                      )}
                    </td>
                    <td className="px-4 md:px-6 py-4 text-gray-900">
                      {adunanza.formattedTime || "—"}
                    </td>
                    <td className="px-4 md:px-6 py-4">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatoBadgeClasses(
                          adunanza.stato
                        )}`}
                      >
                        {adunanza.stato === "inattiva"
                          ? "disattivata"
                          : "attiva"}
                      </span>
                    </td>
                    <td className="px-4 md:px-6 py-4 text-right text-sm font-medium">
                      <div className="flex flex-wrap items-center gap-3 justify-end">
                        <label className="inline-flex items-center cursor-pointer select-none">
                          <span className="text-xs text-gray-500 mr-2">
                            {adunanza.stato === "attiva"
                              ? "Attiva"
                              : "Disattivata"}
                          </span>
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={adunanza.stato === "attiva"}
                            onChange={() => handleToggleStato(adunanza)}
                            disabled={togglingId === adunanza.id}
                          />
                          <div
                            className={`w-10 h-5 rounded-full transition-colors ${
                              adunanza.stato === "attiva"
                                ? "bg-green-500"
                                : "bg-gray-300"
                            }`}
                          >
                            <div
                              className={`h-5 w-5 bg-white rounded-full shadow transform transition-transform ${
                                adunanza.stato === "attiva"
                                  ? "translate-x-5"
                                  : "translate-x-0"
                              }`}
                            ></div>
                          </div>
                        </label>
                        <button
                          onClick={() => openModal(adunanza)}
                          className="text-primary-600 hover:text-primary-800"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => deleteAdunanza(adunanza.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
              </div>
            </>
        )}
      </div>
      )}

      {modalOpen && canManage && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl overflow-y-auto max-h-[90vh] p-4 md:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                {editing ? "Modifica Adunanza" : "Nuova Adunanza"}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="form-label">Giorno della settimana *</label>
                <select
                  className="form-input"
                  value={formData.giorno_settimana}
                  onChange={(e) =>
                    handleFormChange("giorno_settimana", parseInt(e.target.value, 10))
                  }
                >
                  {giorniSettimana
                    .map((label, index) => ({ label, value: index }))
                    .filter((item) => item.value > 0)
                    .map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                </select>
              </div>
              <div className="flex flex-col">
                <label className="form-label">Ora *</label>
                <input
                  type="time"
                  required
                  className="form-input"
                  value={formData.ora_inizio}
                  onChange={(e) =>
                    handleFormChange("ora_inizio", e.target.value)
                  }
                />
              </div>
              <div className="sm:col-span-2 flex flex-col">
                <label className="form-label">Luogo *</label>
                <input
                  type="text"
                  required
                  className="form-input"
                  value={formData.luogo}
                  onChange={(e) => handleFormChange("luogo", e.target.value)}
                />
              </div>
              <div className="sm:col-span-2 flex flex-col">
                <label className="form-label">Indirizzo</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.indirizzo}
                  onChange={(e) =>
                    handleFormChange("indirizzo", e.target.value)
                  }
                />
              </div>
              <div className="sm:col-span-2 flex flex-col">
                <label className="form-label">Stato</label>
                <select
                  className="form-input"
                  value={formData.stato}
                  onChange={(e) => handleFormChange("stato", e.target.value)}
                >
                  <option value="attiva">Attiva</option>
                  <option value="inattiva">Disattivata</option>
                </select>
              </div>
              <div className="sm:col-span-2 flex flex-col">
                <label className="form-label">Note</label>
                <textarea
                  rows={3}
                  className="form-input"
                  value={formData.note}
                  onChange={(e) => handleFormChange("note", e.target.value)}
                  placeholder="Dettagli aggiuntivi"
                />
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={saveAdunanza}
                disabled={savingModal}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {savingModal ? "Salvataggio..." : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdunanzeServizio;
